import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/** Shared "Change Candidate Type" flow — changes the OPPORTUNITY (never the Person),
 * previews the new default pipeline, confirms the stage, preserves all history + audit. */
export function ChangeCandidateTypeDialog({ oid, target, current, ctLabel, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [stageId, setStageId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/opportunities/${oid}/candidate-type-preview`, { params: { candidate_type: target } })
      .then((r) => { setPreview(r.data); setStageId(r.data.suggested_stage_id || ""); })
      .catch((e) => toast.error(apiError(e)));
  }, [oid, target]);
  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/opportunities/${oid}/change-candidate-type`, {
        candidate_type: target, pipeline_id: preview?.pipeline?.id || null,
        stage_id: preview?.pipeline_changes ? stageId : null,
      });
      toast.success("Candidate Type updated"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const stages = (preview?.pipeline?.stages || []).slice().sort((a, b) => a.order - b.order);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="change-candidate-type-dialog">
        <DialogHeader><DialogTitle>Change Candidate Type</DialogTitle>
          <DialogDescription>Changing to <b>{ctLabel(target)}</b>. This updates the Recruiting Opportunity, not the Person. History is preserved.</DialogDescription></DialogHeader>
        {!preview ? <PageLoader /> : (
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <p><span className="text-slate-500">Current pipeline:</span> {current.pipeline_name || current.pipeline?.name || "—"} · <span className="text-slate-500">stage</span> {current.current_stage_name}</p>
              <p className="mt-1"><span className="text-slate-500">New default pipeline:</span> <b>{preview.pipeline?.name || "—"}</b></p>
            </div>
            {preview.pipeline_changes ? (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">Select the current stage in the new pipeline</p>
                <Select value={stageId} onValueChange={setStageId}>
                  <SelectTrigger data-testid="ct-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1.5">Previous candidate type, pipeline &amp; stage history are preserved.</p>
              </div>
            ) : <p className="text-slate-500">Pipeline stays the same; only the candidate type changes.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={saving || (preview?.pipeline_changes && !stageId)} data-testid="ct-confirm" className="bg-brand hover:bg-brand-strong">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
